// Iter 60 — "Confirm column mapping" panel.
// Shown when the universal CSV importer cannot confidently auto-map a
// broker's columns. Displays the LLM's best-guess mapping + the original
// column headers + a few sample data rows so the user can verify or
// override each mapping via dropdown, then re-submit.
import React, { useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PER_FILL_FIELDS = [
  { name: "timestamp",    label: "Date / Time",       required: true },
  { name: "symbol",       label: "Symbol / Contract", required: true },
  { name: "side",         label: "Side (Buy/Sell)",   required: true },
  { name: "quantity",     label: "Quantity",          required: true },
  { name: "price",        label: "Price",             required: true },
  { name: "fees",         label: "Fees / Charges",    required: false },
  { name: "realized_pnl", label: "Realised P&L",      required: false },
  { name: "order_status", label: "Order Status",      required: false },
  { name: "order_id",     label: "Order ID",          required: false },
  { name: "currency",     label: "Currency",          required: false },
];

const PER_TRADE_FIELDS = [
  { name: "entry_time",   label: "Entry Time",        required: true },
  { name: "exit_time",    label: "Exit Time",         required: true },
  { name: "symbol",       label: "Symbol / Contract", required: true },
  { name: "side",         label: "Side (Buy/Sell)",   required: true },
  { name: "quantity",     label: "Quantity",          required: true },
  { name: "entry_price",  label: "Entry Price",       required: true },
  { name: "exit_price",   label: "Exit Price",        required: true },
  { name: "fees",         label: "Fees / Charges",    required: false },
  { name: "realized_pnl", label: "Realised P&L",      required: false },
  { name: "currency",     label: "Currency",          required: false },
];

function badge(conf) {
  const v = Number(conf || 0);
  if (v >= 0.9) return { label: "high", color: "var(--bt-win)" };
  if (v >= 0.7) return { label: "ok",   color: "var(--bt-warn)" };
  return { label: "low", color: "var(--bt-loss)" };
}

export default function ConfirmMappingPanel({ parseResult, broker, onConfirmed, onCancel }) {
  const headers = parseResult?.headers || [];
  const sampleRows = parseResult?.sample_rows || [];
  const sideValues = parseResult?.mapping?.side_values || { buy: ["buy"], sell: ["sell"] };
  const isPerFill = parseResult?.mapping?.is_per_fill !== false; // default true
  const STANDARD_FIELDS = isPerFill ? PER_FILL_FIELDS : PER_TRADE_FIELDS;
  const csvText = parseResult?.csv_echo || "";

  // Iter 71 — Column descriptors keyed by INDEX (not header name) so the
  // dropdown can distinguish duplicate / blank headers. Each entry pairs
  // the column's index with its header text + first-row sample value so
  // the user can identify "Column 9: 0.12" unambiguously.
  // NB: backend returns `sample_rows` as a list of positional arrays
  // (verified iter-71 testing): `sample_rows[0] == ['xxx','yyy',...]` —
  // index-based lookup is mandatory, header-name lookup returns undef.
  const columnDescriptors = useMemo(() => {
    return headers.map((h, idx) => {
      const sample = sampleRows[0]?.[idx];
      const sampleStr =
        sample === undefined || sample === null || sample === ""
          ? "—"
          : String(sample).length > 32
            ? String(sample).slice(0, 32) + "…"
            : String(sample);
      const headerStr = (h ?? "").trim() || "<no header>";
      return {
        idx,
        header: h ?? "",
        sample: sampleStr,
        label: `Column ${idx + 1}: ${sampleStr}  ·  ${headerStr}`,
      };
    });
  }, [headers, sampleRows]);

  // Iter 71 — Lazy `useState` initialiser so this only runs ONCE per
  // component mount. Prior code used a `useMemo` + `useState(initial)`
  // which silently wiped the user's in-progress selections any time the
  // parent re-rendered with a fresh `parseResult` (e.g. after a failed
  // re-parse). The lazy initialiser preserves mapping across re-renders.
  const [mapping, setMapping] = useState(() => {
    const m = {};
    (parseResult?.mapping?.fields || []).forEach((f) => {
      if (!f?.column) return;
      const matchIdx = headers.findIndex((h) => h === f.column);
      if (matchIdx >= 0) m[f.target] = matchIdx;
    });
    return m;
  });
  const [submitting, setSubmitting] = useState(false);
  // Iter 76 — Temporary debug panel state. Toggle via either:
  //   localStorage.setItem('bt-debug-csv', '1')   then refresh, OR
  //   append ?debug=csv to the URL.
  // Renders a pre-parse mapping config + post-parse per-row breakdown
  // with PASS/FAIL + reason for every CSV row, plus copy-to-clipboard.
  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      if (window.localStorage?.getItem("bt-debug-csv") === "1") return true;
      const sp = new URLSearchParams(window.location.search);
      return sp.get("debug") === "csv";
    } catch {
      return false;
    }
  }, []);
  const [debugData, setDebugData] = useState(null);
  // Iter 76 — When debug is on we stash the parse response and only
  // call onConfirmed after the user clicks Continue inside DebugPanel,
  // so the panel stays mounted long enough to display the per-row data.
  const [pendingConfirmed, setPendingConfirmed] = useState(null);

  const fieldsByTarget = Object.fromEntries(
    (parseResult?.mapping?.fields || []).map((f) => [f.target, f]),
  );

  // Has every required (critical) field been mapped?
  const requiredMissing = STANDARD_FIELDS
    .filter((f) => f.required)
    .filter((f) => mapping[f.name] === undefined || mapping[f.name] === null || mapping[f.name] === "")
    .map((f) => f.label);

  const onChange = (target, raw) => {
    // raw is the <select> value (string). "" = unmapped; otherwise parse to index.
    if (raw === "" || raw === null || raw === undefined) {
      setMapping((m) => {
        const next = { ...m };
        delete next[target];
        return next;
      });
      return;
    }
    const idx = Number(raw);
    if (!Number.isInteger(idx) || idx < 0 || idx >= headers.length) return;
    // Iter 75 — Each field's column is INDEPENDENT. We never mutate any
    // other field's value as a side-effect of editing one. Iter-72's
    // auto-clear cascade was removed because users perceived it (rightly)
    // as fields "shifting" on their own. Duplicate selection is still
    // prevented at the UI level via `disabled` on conflicting <option>s
    // (see the dropdown render below) — picking a column already owned
    // by another field is simply not possible without first clearing
    // that other field.
    setMapping((m) => ({ ...m, [target]: idx }));
  };

  // Iter 72 — Type-sanity validation. Sniff each mapped column's first
  // sample value and assert it loosely matches the expected shape. Used
  // to disable "Confirm & parse" when something obviously wrong is
  // mapped (e.g. a date column landed in the Price field).
  const typeErrors = useMemo(() => {
    const errors = {};
    const peek = (idx) => {
      // Concat up to 5 non-empty sample values from row 1..N so a single
      // blank cell doesn't trigger a false positive.
      const samples = [];
      for (let r = 0; r < sampleRows.length && samples.length < 5; r += 1) {
        const v = sampleRows[r]?.[idx];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          samples.push(String(v).trim());
        }
      }
      return samples;
    };
    const isNumericSample = (s) => {
      // Allow comma-thousands, $-prefix, leading sign, and "1,234.56" form.
      const cleaned = s.replace(/[$,\s]/g, "");
      return cleaned !== "" && !Number.isNaN(Number(cleaned));
    };
    const isDateSample = (s) =>
      // ISO 8601 / DD-MM-YYYY / MM/DD/YYYY / "YYYY-MM-DD HH:MM:SS"
      /\d{2,4}[-/]\d{1,2}[-/]\d{1,4}|\d{4}-\d{2}-\d{2}T/.test(s);
    const validators = {
      timestamp:    (s) => isDateSample(s) || !isNumericSample(s),
      entry_time:   (s) => isDateSample(s),
      exit_time:    (s) => isDateSample(s),
      price:        (s) => isNumericSample(s) && !isDateSample(s),
      entry_price:  (s) => isNumericSample(s) && !isDateSample(s),
      exit_price:   (s) => isNumericSample(s) && !isDateSample(s),
      quantity:     (s) => isNumericSample(s) && !isDateSample(s),
      fees:         (s) => isNumericSample(s) && !isDateSample(s),
      realized_pnl: (s) => isNumericSample(s) && !isDateSample(s),
      currency:     (s) => /^[A-Z]{3}$/.test(s.toUpperCase()) || s.length <= 6,
      side:         (s) => /^(buy|sell|long|short|b|s|bought|sold)$/i.test(s.trim()),
      symbol:       (s) => /^[A-Za-z][A-Za-z0-9._/-]*$/.test(s),
    };
    for (const [target, idx] of Object.entries(mapping)) {
      const v = validators[target];
      if (!v) continue;
      const samples = peek(idx);
      if (samples.length === 0) continue; // nothing to validate against
      // Require ALL non-empty samples to look right (defensive).
      const allOk = samples.every(v);
      if (!allOk) {
        const fieldLabel =
          STANDARD_FIELDS.find((f) => f.name === target)?.label || target;
        const colLabel = columnDescriptors[idx]?.label || `Column ${idx + 1}`;
        errors[target] = `${fieldLabel} → ${colLabel}: sample value "${samples[0]}" doesn't look right for this field.`;
      }
    }
    return errors;
  }, [mapping, sampleRows, columnDescriptors, STANDARD_FIELDS]);

  const submit = async () => {
    if (requiredMissing.length) {
      toast.error(`Missing required field${requiredMissing.length > 1 ? "s" : ""}: ${requiredMissing.join(", ")}`);
      // Iter 74 — Scroll the first unmapped row into view so the user
      // can see which dropdown is empty (helps when the panel is long).
      const firstMissing = STANDARD_FIELDS.find(
        (f) => f.required && (mapping[f.name] === undefined || mapping[f.name] === null || mapping[f.name] === ""),
      );
      if (firstMissing) {
        document
          .querySelector(`[data-testid="confirm-mapping-row-${firstMissing.name}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    if (Object.keys(typeErrors).length > 0) {
      const first = Object.keys(typeErrors)[0];
      const label = STANDARD_FIELDS.find((f) => f.name === first)?.label || first;
      toast.error(`"${label}" looks mismatched — ${typeErrors[first]}`);
      document
        .querySelector(`[data-testid="confirm-mapping-row-${first}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      // Resolve column INDEX → header NAME at submit time. The backend's
      // apply_mapping reads field.column as a header string.
      const fields = Object.entries(mapping)
        .filter(([, idx]) => idx !== undefined && idx !== null && idx !== "")
        .map(([target, idx]) => ({
          target,
          column: headers[Number(idx)],
          confidence: 1.0,
          reasoning: "user-confirmed",
        }))
        .filter((f) => !!f.column);
      const { data } = await api.post("/trade-doctor/parse-with-mapping", {
        csv_text: csvText,
        broker,
        mapping: { is_per_fill: isPerFill, side_values: sideValues, fields, warnings: [] },
        debug: debugEnabled,
      });
      // Iter 76 — When the debug flag is ON, we capture the debug payload
      // and KEEP the panel mounted so the user can actually read the
      // per-row output. Otherwise the parent auto-collapses the panel
      // (status='done') and unmounts us in the same React commit before
      // the debug UI ever paints. The "Continue with N trades" button
      // inside DebugPanel finalises by calling onConfirmed.
      if (debugEnabled && data?.debug) {
        setDebugData(data.debug);
        // Stash the response so the Continue button can pass it up.
        setPendingConfirmed(data);
        console.log("[bt-debug-csv] parse-with-mapping response:", data.debug);
        if (data.trade_count > 0) {
          toast.success(
            `Parsed ${data.trade_count} trade${data.trade_count === 1 ? "" : "s"} — review debug then click Continue`,
          );
        } else {
          toast.error("0 trades produced — review per-row debug to see why each row was rejected.");
        }
        return; // skip auto-confirm; user reviews then clicks Continue.
      }
      if (data.trade_count > 0) {
        toast.success(`Parsed ${data.trade_count} trade${data.trade_count === 1 ? "" : "s"} from your mapping`);
        onConfirmed?.(data);
      } else {
        toast.error("Mapping confirmed but no trades could be paired. Check that side / quantity / price columns are correct.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Re-parse failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="confirm-mapping-panel"
      className="rounded-md border p-5 space-y-5"
      style={{ background: "var(--bt-surface)", borderColor: "var(--bt-border)" }}
    >
      <header className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: "color-mix(in srgb, var(--bt-warn) 15%, transparent)", color: "var(--bt-warn)" }}
        >
          <AlertTriangle className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-heading text-lg leading-tight">Confirm column mapping</h3>
          <p className="text-sm mt-1" style={{ color: "var(--bt-text-muted)" }}>
            We couldn't fully auto-detect every column in this broker's export.
            Quickly verify each field below — drop-downs are pre-filled with our best guess.
          </p>
          {parseResult?.confidence?.missing_critical?.length > 0 && (
            <p className="text-xs mt-1.5" style={{ color: "var(--bt-loss)" }}>
              Missing: {parseResult.confidence.missing_critical.join(", ")}
            </p>
          )}
          {parseResult?.confidence?.low_confidence_critical?.length > 0 && (
            <p className="text-xs mt-1" style={{ color: "var(--bt-warn)" }}>
              Low confidence: {parseResult.confidence.low_confidence_critical.join(", ")}
            </p>
          )}
          {/* Iter 69 — Per-row format indicator. Removes ambiguity about
              whether the file needs FIFO pairing or each row IS a trade. */}
          <div
            data-testid="confirm-mapping-format-indicator"
            className="mt-3 inline-flex items-center gap-2 rounded-sm px-2.5 py-1.5 text-xs"
            style={{
              background: isPerFill
                ? "color-mix(in srgb, var(--bt-text-muted) 12%, transparent)"
                : "color-mix(in srgb, var(--bt-accent) 12%, transparent)",
              color: isPerFill ? "var(--bt-text-muted)" : "var(--bt-accent)",
              border: "1px solid",
              borderColor: isPerFill
                ? "var(--bt-border)"
                : "color-mix(in srgb, var(--bt-accent) 40%, transparent)",
            }}
          >
            <span aria-hidden="true">{isPerFill ? "≡" : "⇄"}</span>
            <span className="font-medium">Detected format:</span>
            <span>
              {isPerFill
                ? "One row = one order leg (will be FIFO-paired)"
                : "One row = one complete trade (no pairing needed)"}
            </span>
          </div>
        </div>
      </header>

      {/* Mapping editor */}
      <div data-testid="confirm-mapping-fields" className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {STANDARD_FIELDS.map((f) => {
          const orig = fieldsByTarget[f.name];
          const conf = orig?.confidence ?? null;
          const b = conf != null ? badge(conf) : null;
          const typeErr = typeErrors[f.name];
          // Iter 72 — Map columnIdx → target that currently owns it, so
          // we can dim/disable options already assigned to OTHER fields.
          const ownerByIdx = Object.fromEntries(
            Object.entries(mapping)
              .filter(([t]) => t !== f.name)
              .map(([t, idx]) => [idx, t]),
          );
          return (
            <div
              key={f.name}
              className="rounded-sm border p-3"
              style={{
                background: "var(--bt-surface-2)",
                borderColor: typeErr
                  ? "color-mix(in srgb, var(--bt-warn) 60%, transparent)"
                  : "var(--bt-border)",
              }}
              data-testid={`confirm-mapping-row-${f.name}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--bt-text-muted)" }}>
                  {f.label} {f.required && <span style={{ color: "var(--bt-loss)" }}>*</span>}
                </label>
                {b && (
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: b.color }}>
                    {b.label} ({(conf * 100).toFixed(0)}%)
                  </span>
                )}
              </div>
              <select
                data-testid={`confirm-mapping-${f.name}`}
                value={
                  mapping[f.name] === undefined || mapping[f.name] === null
                    ? ""
                    : String(mapping[f.name])
                }
                onChange={(e) => onChange(f.name, e.target.value)}
                className="w-full bg-transparent border rounded-sm px-2 py-1.5 text-sm"
                style={{
                  color: "var(--bt-text)",
                  borderColor: typeErr ? "var(--bt-warn)" : "var(--bt-border)",
                }}
              >
                <option value="">— not in this CSV —</option>
                {columnDescriptors.map((c) => {
                  const otherTarget = ownerByIdx[c.idx];
                  const otherLabel = otherTarget
                    ? STANDARD_FIELDS.find((x) => x.name === otherTarget)?.label || otherTarget
                    : null;
                  return (
                    <option
                      key={c.idx}
                      value={String(c.idx)}
                      // Iter 75 — Disabled options prevent the user
                      // from picking a column already owned by another
                      // field. We never mutate another field's value
                      // as a side-effect — selection is simply blocked
                      // at the UI level. To re-assign the column, the
                      // user must first clear it from the other field.
                      disabled={!!otherLabel}
                    >
                      {c.label}{otherLabel ? `  (used by ${otherLabel})` : ""}
                    </option>
                  );
                })}
              </select>
              {typeErr && (
                <p
                  className="text-[11px] mt-1.5"
                  style={{ color: "var(--bt-warn)" }}
                  data-testid={`confirm-mapping-typeerror-${f.name}`}
                >
                  ⚠ {typeErr}
                </p>
              )}
              {orig?.reasoning && !typeErr && (
                <p className="text-[11px] mt-1.5" style={{ color: "var(--bt-text-muted)" }}>
                  {orig.reasoning}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Sample preview */}
      {sampleRows.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--bt-text-muted)" }}>
            Sample data (first {sampleRows.length} rows)
          </p>
          <div className="overflow-x-auto rounded-sm border" style={{ borderColor: "var(--bt-border)" }}>
            <table className="text-xs w-full" data-testid="confirm-mapping-preview">
              <thead style={{ background: "var(--bt-surface-2)" }}>
                <tr>
                  {headers.map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left font-mono whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleRows.map((row, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--bt-border)" }}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1.5 font-mono whitespace-nowrap">{String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <footer className="flex flex-wrap gap-2 justify-end items-center">
        {/* Iter 74 — Always-visible blocker hint. Previously the Confirm
            button was silently `disabled` whenever a required field was
            unmapped OR a sample type-mismatch existed, leaving the user
            with no idea why nothing happened on click. Now we ALWAYS
            allow the click; the existing toasts in submit() name the
            blocker, AND this line surfaces it pre-emptively. */}
        {(requiredMissing.length > 0 || Object.keys(typeErrors).length > 0) && (
          <div
            data-testid="confirm-mapping-blocker"
            className="text-xs mr-auto"
            style={{ color: "var(--bt-warn)" }}
          >
            {requiredMissing.length > 0
              ? `Required field${requiredMissing.length > 1 ? "s" : ""} unmapped: ${requiredMissing.join(", ")}`
              : `Fix mismatched field${Object.keys(typeErrors).length > 1 ? "s" : ""}: ${Object.keys(typeErrors)
                  .map((t) => STANDARD_FIELDS.find((f) => f.name === t)?.label || t)
                  .join(", ")}`}
          </div>
        )}
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="confirm-mapping-cancel"
            className="border-[var(--bt-border)]"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={submit}
          disabled={submitting}
          data-testid="confirm-mapping-submit"
          className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
        >
          {submitting ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          {submitting ? "Re-parsing…" : "Confirm & parse"}
        </Button>
      </footer>

      {/* Iter 76 — TEMPORARY debug panel. Toggle via:
            localStorage.setItem('bt-debug-csv', '1')  then refresh
            OR ?debug=csv in URL
          Renders BEFORE parsing: current mapping config + ⚠ DUPLICATE
          flags. Renders AFTER "Confirm & parse" click: full per-row
          PASS/FAIL + reason output + rejection histogram + copy button.
          Easily removable: delete this block + the `debugEnabled` /
          `debugData` useMemo + useState above + the `debug: debugEnabled`
          line in the submit() POST body. */}
      {debugEnabled && (
        <DebugPanel
          mapping={mapping}
          columnDescriptors={columnDescriptors}
          STANDARD_FIELDS={STANDARD_FIELDS}
          isPerFill={isPerFill}
          debugData={debugData}
          pendingConfirmed={pendingConfirmed}
          onContinue={() => {
            if (pendingConfirmed) onConfirmed?.(pendingConfirmed);
            setPendingConfirmed(null);
          }}
        />
      )}
    </section>
  );
}

// Iter 76 — Debug panel sub-component (kept in same file so it's trivial
// to remove later by deleting from this comment block to EOF).
function DebugPanel({ mapping, columnDescriptors, STANDARD_FIELDS, isPerFill, debugData, pendingConfirmed, onContinue }) {
  // Build mapping config with duplicate detection. Two fields pointing at
  // the same column idx is flagged with ⚠ DUPLICATE so the user can spot
  // accidental overlaps that would otherwise silently produce wrong P&L.
  const usageCount = {};
  Object.values(mapping).forEach((idx) => {
    if (idx === undefined || idx === null || idx === "") return;
    usageCount[idx] = (usageCount[idx] || 0) + 1;
  });
  const mappingConfig = STANDARD_FIELDS.map((f) => {
    const idx = mapping[f.name];
    const hasIdx = idx !== undefined && idx !== null && idx !== "";
    const dup = hasIdx && usageCount[idx] > 1;
    const col = hasIdx ? columnDescriptors[Number(idx)] : null;
    return {
      target: f.name,
      label: f.label,
      required: f.required,
      idx: hasIdx ? Number(idx) : null,
      header: col?.header || null,
      sample: col?.sample || null,
      duplicate: dup,
    };
  });

  const buildText = () => {
    const lines = [];
    lines.push("=== BrainTRADES CSV Parser — Debug Output ===");
    lines.push(`Format: ${isPerFill ? "per-fill (FIFO pairing)" : "per-trade"}`);
    lines.push("");
    lines.push("--- Mapping Configuration ---");
    mappingConfig.forEach((m) => {
      const dupTag = m.duplicate ? "  ⚠ DUPLICATE" : "";
      const colStr = m.idx == null
        ? "unmapped"
        : `Column ${m.idx + 1} (${m.header || "<no header>"})  sample="${m.sample || ""}"`;
      lines.push(`  ${m.target}${m.required ? "*" : ""}: ${colStr}${dupTag}`);
    });
    if (!debugData) {
      lines.push("");
      lines.push("(Parse not yet run. Click 'Confirm & parse' to see per-row details.)");
      return lines.join("\n");
    }
    lines.push("");
    lines.push("--- Summary ---");
    lines.push(`  Total rows read:   ${debugData.total_rows}`);
    lines.push(`  Rows passed:       ${debugData.rows_passed}`);
    lines.push(`  Rows rejected:     ${debugData.rows_rejected}`);
    lines.push(`  Trades produced:   ${debugData.trades_produced}`);
    lines.push("  Rejection reasons:");
    Object.entries(debugData.rejection_reasons || {}).forEach(([k, v]) => {
      lines.push(`    ${k}: ${v}`);
    });
    lines.push("");
    lines.push("--- Per-row Details ---");
    (debugData.row_details || []).forEach((d) => {
      lines.push(`\nRow #${d.row_number}  [${(d.status || "?").toUpperCase()}]${d.reason ? "  reason=" + d.reason : ""}`);
      lines.push("  raw:    " + JSON.stringify(d.raw));
      lines.push("  mapped: " + JSON.stringify(d.mapped));
      if (d.trade) lines.push("  trade:  " + JSON.stringify(d.trade));
      if (d.fill)  lines.push("  fill:   " + JSON.stringify(d.fill));
    });
    return lines.join("\n");
  };

  const onCopy = async () => {
    const text = buildText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Debug output copied to clipboard");
    } catch {
      toast.error("Copy failed — see browser console for full text");
      console.log("[bt-debug-csv] full debug text:\n" + text);
    }
  };

  return (
    <section
      data-testid="confirm-mapping-debug-panel"
      className="mt-4 rounded-md border p-4 space-y-3"
      style={{
        background: "color-mix(in srgb, var(--bt-warn) 6%, transparent)",
        borderColor: "color-mix(in srgb, var(--bt-warn) 40%, transparent)",
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-heading text-base" style={{ color: "var(--bt-warn)" }}>
            🛠 Debug Panel (temporary)
          </h4>
          <p className="text-[11px]" style={{ color: "var(--bt-text-muted)" }}>
            Disable: <code>localStorage.removeItem(&apos;bt-debug-csv&apos;)</code> then refresh.
          </p>
        </div>
        <Button
          onClick={onCopy}
          variant="outline"
          data-testid="confirm-mapping-debug-copy"
          className="text-xs border-[var(--bt-warn)]"
          style={{ color: "var(--bt-warn)" }}
        >
          Copy debug output
        </Button>
      </div>

      {/* Pre-parse: mapping config */}
      <div data-testid="confirm-mapping-debug-config">
        <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: "var(--bt-text-muted)" }}>
          Current mapping configuration
        </p>
        <div className="rounded-sm border overflow-x-auto" style={{ borderColor: "var(--bt-border)" }}>
          <table className="text-xs w-full font-mono">
            <thead style={{ background: "var(--bt-surface-2)" }}>
              <tr>
                <th className="px-2 py-1 text-left">Field</th>
                <th className="px-2 py-1 text-left">Column idx</th>
                <th className="px-2 py-1 text-left">Header</th>
                <th className="px-2 py-1 text-left">Sample</th>
                <th className="px-2 py-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {mappingConfig.map((m) => (
                <tr key={m.target} style={{ borderTop: "1px solid var(--bt-border)" }}>
                  <td className="px-2 py-1">{m.target}{m.required && <span style={{ color: "var(--bt-loss)" }}>*</span>}</td>
                  <td className="px-2 py-1">{m.idx == null ? "—" : `${m.idx + 1}`}</td>
                  <td className="px-2 py-1">{m.header ?? "—"}</td>
                  <td className="px-2 py-1">{m.sample ?? "—"}</td>
                  <td className="px-2 py-1">
                    {m.idx == null ? (
                      <span style={{ color: "var(--bt-text-muted)" }}>unmapped</span>
                    ) : m.duplicate ? (
                      <span style={{ color: "var(--bt-warn)" }}>⚠ DUPLICATE</span>
                    ) : (
                      <span style={{ color: "var(--bt-win)" }}>ok</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Post-parse summary + per-row */}
      {debugData && (
        <>
          <div data-testid="confirm-mapping-debug-summary" className="rounded-sm border p-2.5 text-xs font-mono" style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface-2)" }}>
            <div className="grid grid-cols-4 gap-x-3 gap-y-1">
              <div className="opacity-70">Total rows</div><div>{debugData.total_rows}</div>
              <div className="opacity-70">Trades produced</div><div>{debugData.trades_produced}</div>
              <div className="opacity-70" style={{ color: "var(--bt-win)" }}>Passed</div><div>{debugData.rows_passed}</div>
              <div className="opacity-70" style={{ color: "var(--bt-loss)" }}>Rejected</div><div>{debugData.rows_rejected}</div>
            </div>
            {Object.keys(debugData.rejection_reasons || {}).length > 0 && (
              <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--bt-border)" }}>
                <div className="opacity-70 mb-1">Rejection reasons:</div>
                {Object.entries(debugData.rejection_reasons).map(([k, v]) => (
                  <div key={k}>  {k}: {v}</div>
                ))}
              </div>
            )}
          </div>
          <details
            data-testid="confirm-mapping-debug-rows"
            className="rounded-sm border"
            style={{ borderColor: "var(--bt-border)" }}
          >
            <summary className="cursor-pointer select-none px-2 py-1.5 text-xs" style={{ color: "var(--bt-text-muted)" }}>
              Per-row details ({debugData.row_details?.length || 0} rows) — click to expand
            </summary>
            <div className="max-h-96 overflow-auto p-2 space-y-2 text-[11px] font-mono">
              {(debugData.row_details || []).map((d, i) => (
                <div
                  key={i}
                  className="rounded-sm border p-2"
                  style={{
                    borderColor: d.status === "pass" ? "color-mix(in srgb, var(--bt-win) 30%, transparent)" : "color-mix(in srgb, var(--bt-loss) 30%, transparent)",
                    background: d.status === "pass" ? "color-mix(in srgb, var(--bt-win) 4%, transparent)" : "color-mix(in srgb, var(--bt-loss) 4%, transparent)",
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ color: d.status === "pass" ? "var(--bt-win)" : "var(--bt-loss)" }}>
                      Row #{d.row_number} — {String(d.status).toUpperCase()}
                    </span>
                    {d.reason && (
                      <span style={{ color: "var(--bt-loss)" }}>{d.reason}</span>
                    )}
                  </div>
                  <div className="opacity-70 break-all"><b>raw:</b> {JSON.stringify(d.raw)}</div>
                  <div className="opacity-90 break-all mt-0.5"><b>mapped:</b> {JSON.stringify(d.mapped)}</div>
                  {d.trade && <div className="opacity-90 break-all mt-0.5" style={{ color: "var(--bt-win)" }}><b>trade:</b> {JSON.stringify(d.trade)}</div>}
                  {d.fill && <div className="opacity-90 break-all mt-0.5" style={{ color: "var(--bt-win)" }}><b>fill:</b> {JSON.stringify(d.fill)}</div>}
                </div>
              ))}
            </div>
          </details>
          {/* Iter 76 — Continue button: only visible when there's a
              pending confirmation. Clicking finalises the parse (parent
              receives onConfirmed and removes this panel from the tree). */}
          {pendingConfirmed && (
            <div className="flex justify-end pt-1">
              <Button
                onClick={onContinue}
                data-testid="confirm-mapping-debug-continue"
                className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
              >
                Continue with {pendingConfirmed.trade_count || 0} trade{(pendingConfirmed.trade_count || 0) === 1 ? "" : "s"}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
